import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { brand } from "@/lib/brand";
import { getProductById } from "@/lib/product-data";

type SeoConfig = {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
};

const defaultDescription =
  "BRECCS Zimbabwe — Business, Records, Education, Commerce, Care & Services. The operating infrastructure behind every school. Know what is happening. Know what is at risk. Know what to do next.";

const pages: Record<string, Omit<SeoConfig, "path">> = {
  "/": {
    title: "BRECCS Zimbabwe | The Operating Infrastructure Behind Every School",
    description: defaultDescription,
  },
  "/parents": {
    title: "Family Experience | BRECCS Zimbabwe",
    description:
      "Balances, notices, meals, transport, boarding, consents, and school communication for Zimbabwean families — in one place.",
  },
  "/schools": {
    title: "School Operations Infrastructure | BRECCS Zimbabwe",
    description:
      "One control plane for school money, records, education, procurement, feeding, canteens, transport, and boarding across every campus.",
  },
  "/merchants": {
    title: "Cashless School Commerce | BRECCS Zimbabwe",
    description:
      "Controlled learner wallets, canteen transactions, inventory deduction, and daily settlement for Zimbabwean school merchants.",
  },
  "/about": {
    title: "About BRECCS | Zimbabwe Education Operations Infrastructure",
    description:
      "BRECCS is the operating infrastructure behind Zimbabwean schools — connecting money, records, education, commerce, care, and services.",
  },
  "/demo": {
    title: "Explore the BRECCS Platform | Interactive Demos",
    description:
      "Explore interactive demonstrations of BRECCS Command, money, supply, nourish, transit, and school operations.",
  },
  "/ecosystem": {
    title: "The BRECCS Product Architecture",
    description:
      "Explore BRECCS modules: Command, Money, Records, Learn, Supply, Nourish, Canteen, Transit, Boarding, Family, Intelligence, Connect, and Institutional.",
  },
  "/insights": {
    title: "Education Operations Intelligence | BRECCS Zimbabwe",
    description:
      "See collections forecasts, stockout risk, meal fulfilment, transport exceptions, and operational metrics for Zimbabwean school groups.",
  },
  "/auth": {
    title: "Sign In to BRECCS Zimbabwe",
    description: "Access your BRECCS education operations portal.",
    noindex: true,
  },
  "/app": {
    title: "BRECCS Command",
    description: "Your BRECCS education operations control centre.",
    noindex: true,
  },
};

function setMeta(name: string, content: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setProperty(property: string, content: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(url: string): void {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = url;
}

function setStructuredData(config: SeoConfig, url: string): void {
  const id = "breccs-structured-data";
  let element = document.getElementById(id);
  if (!element) {
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    element = script;
    document.head.appendChild(script);
  }
  element.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brand.name,
    legalName: brand.company,
    url: `https://${brand.domain}`,
    description: config.description,
    address: {
      "@type": "PostalAddress",
      addressLocality: brand.city,
      addressCountry: brand.country,
    },
    sameAs: [url],
  });
}

export function SiteSeo(): null {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    const normalizedPathname = pathname.replace(/\/$/, "") || "/";
    const productId = normalizedPathname.startsWith("/ecosystem/")
      ? normalizedPathname.split("/")[2]
      : undefined;
    const product = productId ? getProductById(productId) : undefined;
    const page = pages[normalizedPathname] ?? (product
      ? {
          title: `${product.name} | BRECCS Zimbabwe`,
          description: product.description,
        }
      : {
          title: `${brand.name} | Zimbabwe Education Operations Infrastructure`,
          description: defaultDescription,
          noindex: true,
        });
    const config: SeoConfig = { ...page, path: normalizedPathname };
    const url = `https://${brand.domain}${normalizedPathname === "/" ? "/" : normalizedPathname}`;

    document.title = config.title;
    setMeta("description", config.description);
    setMeta("robots", config.noindex ? "noindex, nofollow" : "index, follow");
    setProperty("og:title", config.title);
    setProperty("og:description", config.description);
    setProperty("og:type", "website");
    setProperty("og:url", url);
    setProperty("og:site_name", brand.name);
    setProperty("og:locale", "en_ZW");
    setMeta("twitter:title", config.title);
    setMeta("twitter:description", config.description);
    setCanonical(url);
    setStructuredData(config, url);
  }, [pathname]);

  return null;
}
